import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import {
  mapApiGender,
  useCarePersons,
} from "@src/packages/patients/hooks/useCarePersons";
import { getPatientGoal } from "@src/packages/patients/patients.types";
import { buildSexualHealthNavigationStack } from "./buildSexualHealthNavigationStack";
import { transformSexualHealthStateToRequest } from "./transformSexualHealthStateToRequest";
import type { SexualHealthFlowRoute } from "./buildSexualHealthNavigationStack";
import type {
  PapSmearProcedureType,
  STDType,
  VaginalInfectionFrequency,
  FrequencyType,
} from "./options";

// ── Sub-resource detail types ──────────────────────────────

export interface PapSmearProcedureDetail {
  id?: string;
  procedure: PapSmearProcedureType;
  other?: string | null;
  procedureDate?: string | null;
  notes?: string | null;
}

export interface STDDetail {
  id?: string;
  stdType: STDType;
  other?: string | null;
  dateOfDiagnosis?: string | null;
  treatment?: string | null;
  notes?: string | null;
}

export interface VaginalInfectionDetail {
  id?: string;
  vaginalInfectionHistory: VaginalInfectionFrequency;
  description?: string | null;
  startDate?: string | null;
  notes?: string | null;
}

// ── State ──────────────────────────────────────────────────

export interface SexualHealthState {
  id?: string;
  // Pap smear
  hadPapSmear: boolean | null;
  papSmearResultAbnormal: boolean | null;
  hadPapSmearProcedures: boolean | null;
  papSmearProcedures: PapSmearProcedureDetail[];
  procedureIdsToDelete: string[];
  // STDs
  hadSTDs: boolean | null;
  stds: STDDetail[];
  stdIdsToDelete: string[];
  // Vaginal infections
  hasVaginalInfectionHistory: boolean | null;
  vaginalInfectionHistories: VaginalInfectionDetail[];
  infectionIdsToDelete: string[];
  // Intercourse
  hasPainfulIntercourse: boolean | null;
  painfulIntercourseScale: number | null;
  // Lubricants
  usesLubricant: boolean | null;
  lubricantName: string | null;
  lubricantUseFrequency: FrequencyType | null;
  // Partner
  isPartnerEjaculatingInVagina: boolean | null;
  vaginalEjaculationFrequency: FrequencyType | null;
  // Erectile difficulties (AMAB-only, stored in maleHistory)
  hasDifficultiesWithErection: boolean | null;
  maleHistorySexualHealthId?: string;
  // Navigation
  navigationStack: SexualHealthFlowRoute[];
  currentNavigationIndex: number;
  // Gender — AFAB-only screens are skipped when this is "male"
  userGender: "male" | "female" | null;
  // Context used to gate questions that only apply to a specific pathway
  partnerGender: "male" | "female" | null;
  partnerChoice: Patient["partnerChoice"];
  treatmentGoal: string | null;
}

const initialState: SexualHealthState = {
  hadPapSmear: null,
  papSmearResultAbnormal: null,
  hadPapSmearProcedures: null,
  papSmearProcedures: [],
  procedureIdsToDelete: [],
  hadSTDs: null,
  stds: [],
  stdIdsToDelete: [],
  hasVaginalInfectionHistory: null,
  vaginalInfectionHistories: [],
  infectionIdsToDelete: [],
  hasPainfulIntercourse: null,
  painfulIntercourseScale: null,
  usesLubricant: null,
  lubricantName: null,
  lubricantUseFrequency: null,
  isPartnerEjaculatingInVagina: null,
  vaginalEjaculationFrequency: null,
  hasDifficultiesWithErection: null,
  navigationStack: ["PapSmear"],
  currentNavigationIndex: 0,
  userGender: null,
  partnerGender: null,
  partnerChoice: null,
  treatmentGoal: null,
};

// ── Resume route ───────────────────────────────────────────

export function getResumeRoute(
  patient: Patient | null | undefined,
  partner?: Patient | null,
): SexualHealthFlowRoute {
  const sh = patient?.obGynHistory?.sexualHealth;
  const userGender = mapApiGender(patient?.gender);
  const partnerGender = mapApiGender(partner?.gender);
  const maleSh = patient?.maleHistory?.sexualHealth;

  if (!sh && !maleSh) {
    return userGender === "male" ? "STDs" : "PapSmear";
  }
  if (!sh && userGender !== "male") {
    return "PapSmear";
  }

  const tempState: SexualHealthState = {
    ...initialState,
    userGender,
    partnerGender,
    partnerChoice: patient?.partnerChoice ?? null,
    treatmentGoal: patient ? getPatientGoal(patient) : null,
    hadPapSmear: sh?.hadPapSmear ?? null,
    papSmearResultAbnormal: sh?.papSmearResultAbnormal ?? null,
    hadPapSmearProcedures: sh?.hadPapSmearProcedures ?? null,
    papSmearProcedures: (sh?.papSmearProcedures ?? []).map((p) => ({
      id: p.id,
      procedure: p.procedure ?? "other",
      other: p.other ?? null,
      procedureDate: p.procedureDate ?? null,
      notes: p.notes ?? null,
    })),
    hadSTDs: sh?.hadSTDs ?? null,
    stds: (sh?.stds ?? []).map((s) => ({
      id: s.id,
      stdType: s.stdType ?? "other",
      other: s.other ?? null,
      dateOfDiagnosis: s.dateOfDiagnosis ?? null,
      treatment: s.treatment ?? null,
      notes: s.notes ?? null,
    })),
    hasVaginalInfectionHistory: sh?.hasVaginalInfectionHistory ?? null,
    vaginalInfectionHistories: (sh?.vaginalInfectionHistories ?? []).map(
      (v) => ({
        id: v.id,
        vaginalInfectionHistory: v.vaginalInfectionHistory ?? "none",
        description: v.description ?? null,
        startDate: v.startDate ?? null,
        notes: v.notes ?? null,
      }),
    ),
    hasPainfulIntercourse: sh?.hasPainfulIntercourse ?? null,
    painfulIntercourseScale: sh?.painfulIntercourseScale ?? null,
    usesLubricant: sh?.usesLubricant ?? null,
    lubricantName: sh?.lubricantName ?? null,
    lubricantUseFrequency: sh?.lubricantUseFrequency ?? null,
    isPartnerEjaculatingInVagina: sh?.isPartnerEjaculatingInVagina ?? null,
    vaginalEjaculationFrequency: sh?.vaginalEjaculationFrequency ?? null,
    hasDifficultiesWithErection: maleSh?.hasDifficultiesWithErection ?? null,
    maleHistorySexualHealthId: maleSh?.id ?? undefined,
  };

  const stack = buildSexualHealthNavigationStack(tempState);

  const screenComplete: Record<SexualHealthFlowRoute, () => boolean> = {
    PapSmear: () =>
      tempState.hadPapSmear !== null &&
      (tempState.hadPapSmear === false ||
        tempState.papSmearResultAbnormal !== null),
    PapSmearProcedures: () => tempState.hadPapSmearProcedures !== null,
    PapSmearProcedureDetails: () => tempState.papSmearProcedures.length > 0,
    STDs: () => tempState.hadSTDs !== null,
    STDDetails: () => tempState.stds.length > 0,
    VaginalInfections: () => tempState.hasVaginalInfectionHistory !== null,
    VaginalInfectionDetails: () =>
      tempState.vaginalInfectionHistories.length > 0,
    PainfulIntercourse: () => tempState.hasPainfulIntercourse !== null,
    Lubricants: () => tempState.usesLubricant !== null,
    PartnerEjaculate: () => tempState.isPartnerEjaculatingInVagina !== null,
    ErectileDifficulties: () => tempState.hasDifficultiesWithErection !== null,
    SexualHealthComplete: () => false,
  };

  for (const route of stack) {
    if (route === "SexualHealthComplete") continue;
    if (!screenComplete[route]()) return route;
  }

  // All screens complete — start from the beginning so users can review
  return stack[0] ?? "PapSmear";
}

// ── Context ────────────────────────────────────────────────

interface SexualHealthContextValue {
  state: SexualHealthState;
  updateState: (updates: Partial<SexualHealthState>) => void;
  resetState: () => void;
  save: () => Promise<void>;
}

const SexualHealthContext = createContext<SexualHealthContextValue | null>(
  null,
);

export function SexualHealthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const { partnerPatient } = useCarePersons();
  const { mutateAsync } = useQueuedPatientUpdate();
  const [state, setState] = useState<SexualHealthState>(initialState);

  // Load data from patient
  useEffect(() => {
    if (isLoading || !patient) return;

    const userGender = mapApiGender(patient?.gender);
    const partnerGender = mapApiGender(partnerPatient?.gender);
    const partnerChoice = patient.partnerChoice ?? null;
    const treatmentGoal = getPatientGoal(patient);
    const sexualHealth = patient?.obGynHistory?.sexualHealth;
    const maleSh = patient?.maleHistory?.sexualHealth;
    if (!sexualHealth && !maleSh) {
      setState((prev) => {
        if (
          prev.userGender === userGender &&
          prev.partnerGender === partnerGender &&
          prev.partnerChoice === partnerChoice &&
          prev.treatmentGoal === treatmentGoal
        ) {
          return prev;
        }
        const seeded: SexualHealthState = {
          ...prev,
          userGender,
          partnerGender,
          partnerChoice,
          treatmentGoal,
        };
        const stack = buildSexualHealthNavigationStack(seeded);
        return {
          ...seeded,
          navigationStack: stack,
          currentNavigationIndex: 0,
        };
      });
      return;
    }

    setState((prev) => {
      const isInitialState =
        prev.hadPapSmear === null &&
        prev.stds.length === 0 &&
        prev.hasDifficultiesWithErection === null;
      if (!isInitialState) return prev;

      const next: SexualHealthState = {
        ...prev,
        userGender,
        partnerGender,
        partnerChoice,
        treatmentGoal,
        id: sexualHealth?.id ?? undefined,
        hadPapSmear: sexualHealth?.hadPapSmear ?? null,
        papSmearResultAbnormal: sexualHealth?.papSmearResultAbnormal ?? null,
        hadPapSmearProcedures: sexualHealth?.hadPapSmearProcedures ?? null,
        papSmearProcedures: (sexualHealth?.papSmearProcedures ?? []).map(
          (p) => ({
            id: p.id ?? undefined,
            procedure: p.procedure ?? "other",
            other: p.other ?? null,
            procedureDate: p.procedureDate ?? null,
            notes: p.notes ?? null,
          }),
        ),
        hadSTDs: sexualHealth?.hadSTDs ?? null,
        stds: (sexualHealth?.stds ?? []).map((s) => ({
          id: s.id ?? undefined,
          stdType: s.stdType ?? "other",
          other: s.other ?? null,
          dateOfDiagnosis: s.dateOfDiagnosis ?? null,
          treatment: s.treatment ?? null,
          notes: s.notes ?? null,
        })),
        hasVaginalInfectionHistory:
          sexualHealth?.hasVaginalInfectionHistory ?? null,
        vaginalInfectionHistories: (
          sexualHealth?.vaginalInfectionHistories ?? []
        ).map((v) => ({
          id: v.id ?? undefined,
          vaginalInfectionHistory: v.vaginalInfectionHistory ?? "none",
          description: v.description ?? null,
          startDate: v.startDate ?? null,
          notes: v.notes ?? null,
        })),
        hasPainfulIntercourse: sexualHealth?.hasPainfulIntercourse ?? null,
        painfulIntercourseScale: sexualHealth?.painfulIntercourseScale ?? null,
        usesLubricant: sexualHealth?.usesLubricant ?? null,
        lubricantName: sexualHealth?.lubricantName ?? null,
        lubricantUseFrequency: sexualHealth?.lubricantUseFrequency ?? null,
        isPartnerEjaculatingInVagina:
          sexualHealth?.isPartnerEjaculatingInVagina ?? null,
        vaginalEjaculationFrequency:
          sexualHealth?.vaginalEjaculationFrequency ?? null,
        hasDifficultiesWithErection:
          maleSh?.hasDifficultiesWithErection ?? null,
        maleHistorySexualHealthId: maleSh?.id ?? undefined,
      };

      const stack = buildSexualHealthNavigationStack(next);
      return {
        ...next,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          prev.currentNavigationIndex,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, [patient, isLoading, partnerPatient]);

  const updateState = useCallback((updates: Partial<SexualHealthState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      const stack = buildSexualHealthNavigationStack(newState);
      return {
        ...newState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          newState.currentNavigationIndex ?? 0,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, []);

  const save = useCallback(async () => {
    const payload = transformSexualHealthStateToRequest(state);
    if (Object.keys(payload).length === 0) return;

    const obGynHistoryId = patient?.obGynHistory?.id;
    if (payload.obGynHistory && obGynHistoryId) {
      payload.obGynHistory.id = obGynHistoryId;
    }

    const maleHistoryId = patient?.maleHistory?.id;
    if (payload.maleHistory && maleHistoryId) {
      payload.maleHistory.id = maleHistoryId;
    }

    try {
      await mutateAsync(payload);
    } catch (error) {
      throw error;
    }
  }, [state, mutateAsync, patient]);

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <SexualHealthContext.Provider
      value={{ state, updateState, resetState, save }}
    >
      {children}
    </SexualHealthContext.Provider>
  );
}

export function useSexualHealth() {
  const ctx = useContext(SexualHealthContext);
  if (!ctx) {
    throw new Error("useSexualHealth must be used within SexualHealthProvider");
  }
  return ctx;
}
